import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { MODELES } from '../utils/constants';
import { toast } from 'sonner';
import { Check, ChevronRight, ChevronLeft, Search, Loader, ExternalLink, RefreshCw, Mail, Globe, ShoppingCart, CheckCircle, AlertCircle, FolderOpen, Clock } from 'lucide-react';

const STEPS = [
  { num: 1, title: 'SIRET', sub: 'Lookup entreprise' },
  { num: 2, title: 'Coordonnées', sub: 'Salon & contact' },
  { num: 3, title: 'Comptable', sub: 'Infos comptable' },
  { num: 4, title: 'Livraison', sub: 'Adresse' },
  { num: 5, title: 'Commande', sub: 'Modèle & qté' },
  { num: 6, title: 'Commercial', sub: 'Affectation' },
  { num: 7, title: 'Google Drive', sub: 'Dossier auto' },
  { num: 8, title: 'Domaine', sub: 'Achat auto' },
  { num: 9, title: 'Config Email', sub: 'Guide OVH' },
  { num: 10, title: 'Net Ent.', sub: 'Identifiants' },
];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-1 flex-wrap px-4 py-4">
      {STEPS.map((step, i) => (
        <React.Fragment key={step.num}>
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
              current > step.num ? 'bg-emerald-500 border-emerald-500 text-white' :
              current === step.num ? 'bg-[hsl(222,47%,11%)] border-[hsl(222,47%,11%)] text-white scale-110' :
              'bg-white border-gray-200 text-gray-400'
            }`}>
              {current > step.num ? <Check size={14} /> : step.num}
            </div>
            <span className={`text-xs mt-1 hidden sm:block ${current === step.num ? 'text-[hsl(222,47%,11%)] font-semibold' : 'text-gray-400'}`}>
              {step.title}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 min-w-[12px] max-w-[30px] h-0.5 mb-4 ${current > step.num ? 'bg-emerald-400' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white";

export default function ClientWizard() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [siretLoading, setSiretLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState({
    siret: '', siren: '', societe: '', gerant: '', gerant_nom: '', gerant_prenom: '', 
    numero_tva: '', adresse: '', code_postal: '', ville: '',
    email_salon: '', tel_salon: '', tel_portable: '',
    comptable_nom: '', comptable_email: '', comptable_tel: '',
    adresse_livraison: '', code_postal_livraison: '', ville_livraison: '',
    modele: 'KLOSS', quantite: 1, frais_livraison: 780,
    commercial_id: '',
    identifiants_ne: { siret: '', nom: '', prenom: '', mot_de_passe: '', email_dossier: '' },
    google_drive_folder_id: '', google_drive_folder_url: '',
    domaine: '', domaine_full: '', email_domaine: '', email_domaine_password: '', email_domaine_server: '', 
    domaine_order_id: '', zimbra_slot_included: false, email_domaine_status: '',
  });

  // Domain purchase state
  const [domainSearch, setDomainSearch] = useState({ searching: false, result: null });
  const [domainPurchase, setDomainPurchase] = useState({ purchasing: false, purchased: false, orderId: null });
  
  // Email verification state
  const [emailCheck, setEmailCheck] = useState({ checking: false, ready: false, autoCheck: false });
  const [emailCheckInterval, setEmailCheckInterval] = useState(null);
  
  // Ref to store email config (to avoid async state issues)
  const emailConfigRef = useRef({
    email: '',
    password: '',
    server: 'ssl0.ovh.net',
    status: ''
  });

  const [commande, setCommande] = useState({
    frais_livraison: 780,
    quantities: {}  // { [produit_id]: quantite }
  });
  const [produits, setProduits] = useState([]);

  const updateQty = (produitId, qty) => {
    setCommande(prev => ({ ...prev, quantities: { ...prev.quantities, [produitId]: Math.max(0, qty) } }));
  };

  useEffect(() => {
    api.get('/users/commerciaux/list').then(res => setCommerciaux(res.data)).catch(() => {});
    api.get('/produits').then(res => setProduits(res.data.filter(p => p.actif !== false))).catch(() => {});
    // Load draft if URL param present
    const draftParam = searchParams.get('draft');
    if (draftParam) {
      loadDraft(draftParam);
    }
  }, []);

  const loadDraft = async (id) => {
    setDraftLoading(true);
    try {
      const res = await api.get(`/drafts/${id}`);
      const draft = res.data;
      if (draft && draft.data) {
        const { _commande, _domainPurchased, _domainOrderId, ...clientData } = draft.data;
        setData(prev => ({ ...prev, ...clientData }));
        if (_commande) {
          setCommande(_commande);
        }
        if (_domainPurchased) {
          setDomainPurchase({ purchasing: false, purchased: true, orderId: _domainOrderId || null });
        }
        setStep(draft.current_step || 1);
        setDraftId(id);
        toast.success(`Brouillon chargé — reprise à l'étape ${draft.current_step}`);
      }
    } catch (e) {
      toast.error('Impossible de charger le brouillon');
    } finally {
      setDraftLoading(false);
    }
  };

  const saveDraft = async (currentStep, overrideData = null) => {
    const payload = overrideData || data;
    try {
      const draftData = {
        ...payload,
        _commande: commande,
        _domainPurchased: domainPurchase.purchased,
        _domainOrderId: domainPurchase.orderId
      };
      const res = await api.post('/drafts', {
        draft_id: draftId || null,
        data: draftData,
        current_step: currentStep,
        societe: payload.societe || ''
      });
      if (res.data.id && !draftId) {
        setDraftId(res.data.id);
      }
    } catch (e) {
      console.error('Sauvegarde brouillon échouée:', e);
    }
  };

  const update = (field, value) => setData(prev => ({ ...prev, [field]: value }));
  const updateNE = (field, value) => setData(prev => ({ ...prev, identifiants_ne: { ...prev.identifiants_ne, [field]: value } }));
  const updateCmd = (field, value) => setCommande(prev => ({ ...prev, [field]: value }));

  const lookupSiret = async () => {
    const cleanSiret = data.siret.replace(/\s/g, '');
    if (cleanSiret.length < 9) { 
      toast.error('Entrez au moins 9 chiffres (SIREN)'); 
      return; 
    }
    setSiretLoading(true);
    setSearchResults([]);
    setShowResults(false);
    
    try {
      const res = await api.get(`/sirene/search?q=${cleanSiret}`);
      const results = res.data.results || [];
      
      if (results.length === 0) {
        toast.error('Aucune entreprise trouvée');
        return;
      }
      
      if (results.length === 1) {
        // Single result - auto-fill
        selectCompany(results[0]);
        toast.success('Entreprise trouvée !');
      } else {
        // Multiple results - show list
        setSearchResults(results);
        setShowResults(true);
        toast.info(`${results.length} entreprises trouvées - sélectionnez`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de recherche');
    } finally { 
      setSiretLoading(false); 
    }
  };

  const selectCompany = (company) => {
    const newData = {
      ...data,
      siret: company.siret || data.siret,
      siren: company.siren || data.siren,
      societe: company.societe || data.societe,
      adresse: company.adresse || data.adresse,
      code_postal: company.code_postal || data.code_postal,
      ville: company.ville || data.ville,
      gerant: company.gerant || data.gerant,
      gerant_nom: company.gerant_nom || data.gerant_nom,
      gerant_prenom: company.gerant_prenom || data.gerant_prenom,
      numero_tva: company.numero_tva || data.numero_tva,
      identifiants_ne: {
        ...data.identifiants_ne,
        siret: company.siret,
        nom: company.gerant_nom || '',
        prenom: company.gerant_prenom || ''
      }
    };
    setData(newData);
    setShowResults(false);
    setSearchResults([]);
    setStep(2);
    saveDraft(2, newData);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Get email config from ref (most reliable source)
      const emailConfig = emailConfigRef.current;
      
      // Prepare client data with email info
      const clientData = {
        ...data,
        // Use ref values which are updated synchronously
        email_domaine: emailConfig.email || data.email_domaine || (data.domaine_full ? `contact@${data.domaine_full}` : (data.domaine ? `contact@${data.domaine}.store` : '')),
        email_domaine_password: emailConfig.password || data.email_domaine_password || '',
        email_domaine_server: emailConfig.server || data.email_domaine_server || 'ssl0.ovh.net',
        email_domaine_status: emailConfig.status || data.email_domaine_status || (emailConfig.email ? 'active' : 'pending_configuration'),
      };
      
      console.log('Creating client with email:', clientData.email_domaine); // Debug log
      
      // Create client
      const clientRes = await api.post('/clients', clientData);
      const clientId = clientRes.data.id;
      
      // Create Google Drive folder if not already created
      if (!data.google_drive_folder_id && data.societe) {
        try {
          const driveRes = await api.post('/drive/folder', {
            client_id: clientId,
            client_name: data.societe
          });
          if (driveRes.data.folder_id) {
            // Update client with folder info
            await api.put(`/clients/${clientId}`, {
              google_drive_folder_id: driveRes.data.folder_id,
              google_drive_folder_url: driveRes.data.folder_url
            });
          }
        } catch (driveErr) {
          console.error('Google Drive folder creation failed:', driveErr);
          // Non-blocking - continue with client creation
        }
      }
      
      // Update client with domain info if domain was purchased during wizard
      if (domainPurchase.purchased && domainPurchase.orderId) {
        try {
          await api.put(`/clients/${clientId}`, {
            domaine: data.domaine_full || (data.domaine ? `${data.domaine}.store` : ''),
            domaine_order_id: domainPurchase.orderId,
            domaine_status: 'pending',
            zimbra_slot_included: true,
            email_domaine: data.email_domaine || `contact@${data.domaine_full || data.domaine + '.store'}`,
            email_domaine_status: data.email_domaine_status || 'pending_configuration',
            email_domaine_password: data.email_domaine_password || process.env.REACT_APP_OVH_DEFAULT_EMAIL_PASSWORD || '',
            email_domaine_server: data.email_domaine_server || 'ssl0.ovh.net'
          });
        } catch (domainErr) {
          console.error('Domain info update failed:', domainErr);
        }
      }
      
      // Créer UNE SEULE commande multi-lignes (Option B)
      const lignes = produits
        .filter(p => (commande.quantities[p.id] || 0) > 0)
        .map(p => ({
          produit_id: p.id,
          reference: p.reference,
          designation: p.designation,
          quantite: commande.quantities[p.id],
          prix_unitaire_ht: p.prix_ht
        }));

      if (lignes.length > 0) {
        await api.post('/commandes', {
          client_id: clientId,
          lignes: lignes,
          frais_livraison: commande.frais_livraison,
          commercial_id: data.commercial_id,
          statut: 'incomplet',
          date_commande: new Date().toISOString()
        });
      }
      
      toast.success('Client et commande(s) créés avec succès !');
      // Supprimer le brouillon si existant
      if (draftId) {
        try { await api.delete(`/drafts/${draftId}`); } catch {}
      }
      navigate(`/clients/${clientId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally { setLoading(false); }
  };

  const canGoNext = () => {
    if (step === 1) return data.societe.length > 0;
    return true;
  };

  const handleNext = () => {
    const newStep = step + 1;
    setStep(newStep);
    saveDraft(newStep);
  };

  if (draftLoading) return (
    <div className="min-h-screen bg-[hsl(210,20%,98%)] flex items-center justify-center">
      <div className="text-center">
        <Loader size={28} className="animate-spin text-[hsl(222,47%,11%)] mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Chargement du brouillon...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(210,20%,98%)] py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-2xl border border-[hsl(214,32%,91%)] shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-[hsl(222,47%,11%)] text-white px-6 py-5">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold">
                {data.societe ? data.societe : 'Nouveau client'}
              </h1>
              {draftId && (
                <span className="text-xs text-blue-300 flex items-center gap-1">
                  <Check size={12} />
                  Brouillon sauvegardé
                </span>
              )}
            </div>
            <p className="text-blue-200 text-sm mt-1">Étape {step} sur {STEPS.length} — {STEPS[step-1].title}: {STEPS[step-1].sub}</p>
          </div>

          {/* Steps */}
          <div className="border-b border-gray-100">
            <StepIndicator current={step} />
          </div>

          {/* Content */}
          <div className="p-6 min-h-[320px]">
            {/* Step 1 - SIRET */}
            {step === 1 && (
              <div className="space-y-5">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                  Entrez le <strong>SIREN (9 chiffres)</strong> ou <strong>SIRET (14 chiffres)</strong> pour récupérer automatiquement les données de l'entreprise
                </div>
                <Field label="Numéro SIREN / SIRET" required>
                  <div className="flex gap-2">
                    <input
                      className={inputCls + " flex-1"}
                      value={data.siret}
                      onChange={e => update('siret', e.target.value.replace(/\D/g, ''))}
                      placeholder="812703577 ou 81270357700021"
                      maxLength={14}
                      data-testid="siret-input"
                    />
                    <button
                      onClick={lookupSiret}
                      disabled={siretLoading || data.siret.length < 9}
                      className="flex items-center gap-2 bg-[hsl(222,47%,11%)] text-white px-4 py-2 rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] disabled:opacity-50 transition-colors"
                      data-testid="lookup-siret-btn"
                    >
                      {siretLoading ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                      Chercher
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {data.siret.length}/14 chiffres • {data.siret.length >= 9 ? '✓ SIREN valide' : 'Min 9 chiffres'}
                  </p>
                </Field>

                {/* Search results list */}
                {showResults && searchResults.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-700">{searchResults.length} résultat(s) trouvé(s) - Sélectionnez une entreprise</p>
                    </div>
                    <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                      {searchResults.map((company, idx) => (
                        <button
                          key={company.siret || idx}
                          onClick={() => selectCompany(company)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                          data-testid={`company-result-${idx}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{company.societe}</p>
                              <p className="text-sm text-gray-600">{company.adresse}, {company.code_postal} {company.ville}</p>
                              {company.gerant && (
                                <p className="text-xs text-gray-500 mt-1">Gérant: {company.gerant}</p>
                              )}
                            </div>
                            <div className="text-right ml-3">
                              <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{company.siret}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!showResults && (
                  <>
                    <Field label="Nom de la société" required>
                      <input className={inputCls} value={data.societe} onChange={e => update('societe', e.target.value)} placeholder="Nom du salon" data-testid="societe-input" />
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Gérant">
                        <input className={inputCls} value={data.gerant} onChange={e => update('gerant', e.target.value)} placeholder="Prénom Nom du gérant" />
                      </Field>
                      <Field label="Ville">
                        <input className={inputCls} value={data.ville} onChange={e => update('ville', e.target.value)} placeholder="Paris" />
                      </Field>
                    </div>
                    {data.numero_tva && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                        <p className="text-emerald-800">
                          <span className="font-medium">N° TVA Intracommunautaire :</span> {data.numero_tva}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 2 - Contacts */}
            {step === 2 && (
              <div className="space-y-4">
                <Field label="Adresse">
                  <input className={inputCls} value={data.adresse} onChange={e => update('adresse', e.target.value)} placeholder="162 Rue de..." />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Code postal">
                    <input className={inputCls} value={data.code_postal} onChange={e => update('code_postal', e.target.value)} placeholder="75020" />
                  </Field>
                  <Field label="Ville">
                    <input className={inputCls} value={data.ville} onChange={e => update('ville', e.target.value)} placeholder="Paris" />
                  </Field>
                </div>
                <Field label="Email salon">
                  <input className={inputCls} type="email" value={data.email_salon} onChange={e => update('email_salon', e.target.value)} placeholder="contact@salon.fr" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Tél salon">
                    <input className={inputCls} value={data.tel_salon} onChange={e => update('tel_salon', e.target.value)} placeholder="01 XX XX XX XX" />
                  </Field>
                  <Field label="Tél portable">
                    <input className={inputCls} value={data.tel_portable} onChange={e => update('tel_portable', e.target.value)} placeholder="06 XX XX XX XX" />
                  </Field>
                </div>
                <Field label="Chiffre d'affaires HT">
                  <input className={inputCls} type="number" value={data.chiffre_affaire_ht || ''} onChange={e => update('chiffre_affaire_ht', parseFloat(e.target.value) || 0)} placeholder="150000" />
                </Field>
              </div>
            )}

            {/* Step 3 - Comptable */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">Coordonnées du comptable du client</div>
                <Field label="Nom du comptable">
                  <input className={inputCls} value={data.comptable_nom} onChange={e => update('comptable_nom', e.target.value)} placeholder="Cabinet Dupont" />
                </Field>
                <Field label="Email comptable">
                  <input className={inputCls} type="email" value={data.comptable_email} onChange={e => update('comptable_email', e.target.value)} placeholder="compta@cabinet.fr" />
                </Field>
                <Field label="Tél comptable">
                  <input className={inputCls} value={data.comptable_tel} onChange={e => update('comptable_tel', e.target.value)} placeholder="01 XX XX XX XX" />
                </Field>
              </div>
            )}

            {/* Step 4 - Livraison */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  Adresse de livraison des équipements (si différente de l'adresse du salon)
                </div>
                <Field label="Adresse de livraison (rue)">
                  <input
                    className={inputCls}
                    value={data.adresse_livraison}
                    onChange={e => update('adresse_livraison', e.target.value)}
                    placeholder={data.adresse || "162 Rue de..."}
                    data-testid="adresse-livraison-input"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Code postal livraison">
                    <input
                      className={inputCls}
                      value={data.code_postal_livraison}
                      onChange={e => update('code_postal_livraison', e.target.value)}
                      placeholder={data.code_postal || "75020"}
                      data-testid="cp-livraison-input"
                    />
                  </Field>
                  <Field label="Ville livraison">
                    <input
                      className={inputCls}
                      value={data.ville_livraison}
                      onChange={e => update('ville_livraison', e.target.value)}
                      placeholder={data.ville || "Paris"}
                      data-testid="ville-livraison-input"
                    />
                  </Field>
                </div>
                {!data.adresse_livraison && !data.code_postal_livraison && !data.ville_livraison && data.adresse && (
                  <button 
                    onClick={() => {
                      update('adresse_livraison', data.adresse);
                      update('code_postal_livraison', data.code_postal);
                      update('ville_livraison', data.ville);
                    }}
                    className="text-sm text-blue-600 hover:underline"
                    data-testid="copy-address-btn"
                  >
                    Utiliser l'adresse du salon
                  </button>
                )}
                {(data.adresse_livraison || data.code_postal_livraison || data.ville_livraison) && (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm">
                    <p className="text-gray-600 mb-1">Adresse de livraison :</p>
                    <p className="font-medium text-gray-900">
                      {data.adresse_livraison || '—'}<br/>
                      {data.code_postal_livraison} {data.ville_livraison}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 5 - Commande */}
            {step === 5 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  Sélectionnez les quantités pour chaque produit commandé
                </div>

                {/* Cartes produits dynamiques */}
                {produits.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-sm border border-dashed border-gray-300 rounded-xl">
                    Aucun produit disponible — ajoutez des produits dans le catalogue
                  </div>
                ) : produits.map(p => {
                  const qty = commande.quantities[p.id] || 0;
                  return (
                    <div key={p.id} className={`p-4 border-2 rounded-xl transition-all ${qty > 0 ? 'border-[hsl(222,47%,11%)] bg-[hsl(222,47%,11%)]/5' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-lg text-gray-900">{p.reference}</p>
                          <p className="text-sm text-gray-500">{p.designation}</p>
                          <p className="text-sm font-semibold text-emerald-600 mt-1">
                            {p.prix_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT / unité
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateQty(p.id, qty - 1)}
                            className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-xl font-bold text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                            data-testid={`minus-${p.reference.toLowerCase()}`}
                          >−</button>
                          <span className="w-12 text-center text-2xl font-bold text-gray-900" data-testid={`qty-${p.reference.toLowerCase()}`}>
                            {qty}
                          </span>
                          <button
                            onClick={() => updateQty(p.id, qty + 1)}
                            className="w-10 h-10 rounded-full border-2 border-[hsl(222,47%,11%)] bg-[hsl(222,47%,11%)] flex items-center justify-center text-xl font-bold text-white hover:bg-[hsl(222,47%,18%)] transition-colors"
                            data-testid={`plus-${p.reference.toLowerCase()}`}
                          >+</button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Frais de livraison */}
                <Field label="Frais de livraison + Formation HT (€)">
                  <input
                    className={inputCls}
                    type="number"
                    min="0"
                    value={commande.frais_livraison}
                    onChange={e => setCommande(prev => ({ ...prev, frais_livraison: parseFloat(e.target.value) || 0 }))}
                  />
                </Field>

                {/* Récapitulatif */}
                {(() => {
                  const totalProdHT = produits.reduce((sum, p) => sum + (commande.quantities[p.id] || 0) * p.prix_ht, 0);
                  const totalHT = totalProdHT + commande.frais_livraison;
                  const tva = Math.round(totalHT * 0.20 * 100) / 100;
                  const totalTTC = Math.round((totalHT + tva) * 100) / 100;
                  const subvention = Math.round(totalHT * 0.70 * 100) / 100;
                  const hasProducts = produits.some(p => (commande.quantities[p.id] || 0) > 0);
                  return (
                    <div className="bg-gray-50 rounded-xl p-4 text-sm">
                      <p className="font-semibold text-gray-700 mb-3">Récapitulatif commande</p>
                      {!hasProducts ? (
                        <p className="text-amber-600 text-center py-2">Sélectionnez au moins un produit</p>
                      ) : (
                        <div className="space-y-2 text-gray-600">
                          {produits.filter(p => (commande.quantities[p.id] || 0) > 0).map(p => (
                            <div key={p.id} className="flex justify-between">
                              <span>{commande.quantities[p.id]}x {p.reference}</span>
                              <span className="font-medium">{(commande.quantities[p.id] * p.prix_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT</span>
                            </div>
                          ))}
                          <div className="flex justify-between">
                            <span>Livraison + Formation</span>
                            <span className="font-medium">{commande.frais_livraison.toLocaleString('fr-FR')} € HT</span>
                          </div>
                          <div className="flex justify-between font-semibold text-gray-900 border-t border-gray-200 pt-2">
                            <span>Total HT</span>
                            <span>{totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>TVA 20%</span>
                            <span>{tva.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                          </div>
                          <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-200 pt-2">
                            <span>Total TTC</span>
                            <span>{totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                          </div>
                          <div className="flex justify-between text-emerald-700 font-medium bg-emerald-50 rounded-lg px-3 py-2 mt-2">
                            <span>Subvention CARSAT 70%</span>
                            <span>{subvention.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Step 6 - Commercial */}
            {step === 6 && (
              <div className="space-y-4">
                <Field label="Commercial responsable">
                  {commerciaux.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                      Aucun commercial créé. Vous pouvez en créer dans les Paramètres.
                      <p className="mt-1 text-amber-600">La commande sera assignée à votre compte.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {commerciaux.map(c => (
                        <button
                          key={c.id}
                          onClick={() => update('commercial_id', c.id)}
                          className={`w-full p-3 border-2 rounded-xl text-left transition-all ${data.commercial_id === c.id ? 'border-[hsl(222,47%,11%)] bg-[hsl(222,47%,11%)]/5' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <p className="font-medium text-gray-900">{c.prenom} {c.nom}</p>
                          <p className="text-xs text-gray-500">{c.email}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </Field>
              </div>
            )}

            {/* Step 7 - Google Drive */}
            {step === 7 && (
              <GoogleDriveStep 
                data={data}
                update={update}
              />
            )}

            {/* Step 8 - Domaine + Zimbra */}
            {step === 8 && (
              <DomainStep 
                data={data}
                update={update}
                domainSearch={domainSearch}
                setDomainSearch={setDomainSearch}
                domainPurchase={domainPurchase}
                setDomainPurchase={setDomainPurchase}
              />
            )}

            {/* Step 9 - Email Configuration Guide */}
            {step === 9 && (
              <EmailConfigStep
                data={data}
                update={update}
                setData={setData}
                emailConfigRef={emailConfigRef}
                emailCheck={emailCheck}
                setEmailCheck={setEmailCheck}
                emailCheckInterval={emailCheckInterval}
                setEmailCheckInterval={setEmailCheckInterval}
              />
            )}

            {/* Step 10 - Net Entreprises */}
            {step === 10 && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  Identifiants de connexion Net Entreprises du client (stockés de manière sécurisée)
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Nom">
                    <input className={inputCls} value={data.identifiants_ne.nom} onChange={e => updateNE('nom', e.target.value)} />
                  </Field>
                  <Field label="Prénom">
                    <input className={inputCls} value={data.identifiants_ne.prenom} onChange={e => updateNE('prenom', e.target.value)} />
                  </Field>
                </div>
                <Field label="Email dossier">
                  <div className="relative">
                    <input 
                      className={inputCls} 
                      type="email" 
                      value={data.identifiants_ne.email_dossier || data.email_domaine || (data.domaine_full ? `contact@${data.domaine_full}` : (data.domaine ? `contact@${data.domaine}.store` : ''))} 
                      onChange={e => updateNE('email_dossier', e.target.value)} 
                      placeholder={data.email_domaine || (data.domaine_full ? `contact@${data.domaine_full}` : (data.domaine ? `contact@${data.domaine}.store` : "dossier@salon.fr"))} 
                    />
                    {(data.email_domaine || data.domaine) && !data.identifiants_ne.email_dossier && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">Auto</span>
                    )}
                  </div>
                  {data.email_domaine && (
                    <p className="text-xs text-emerald-600 mt-1">✓ Email configuré à l'étape 9 : <strong>{data.email_domaine}</strong></p>
                  )}
                  {!data.email_domaine && data.domaine && (
                    <p className="text-xs text-gray-500 mt-1">Email supposé basé sur le domaine : contact@{data.domaine_full || `${data.domaine}.store`}</p>
                  )}
                </Field>
                <Field label="Mot de passe Net Entreprises">
                  <input className={inputCls} type="text" value={data.identifiants_ne.mot_de_passe} onChange={e => updateNE('mot_de_passe', e.target.value)} placeholder="Mot de passe" />
                </Field>
                {data.numero_tva && (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm">
                    <p className="text-gray-600">Numéro TVA : <strong>{data.numero_tva}</strong></p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
            <button
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-30 transition-colors"
              data-testid="wizard-prev-btn"
            >
              <ChevronLeft size={16} />
              Précédent
            </button>

            {step < STEPS.length ? (
              <button
                onClick={handleNext}
                disabled={!canGoNext()}
                className="flex items-center gap-2 bg-[hsl(222,47%,11%)] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[hsl(222,47%,18%)] disabled:opacity-50 transition-colors active:scale-95"
                data-testid="wizard-next-btn"
              >
                Suivant
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors active:scale-95"
                data-testid="wizard-submit-btn"
              >
                {loading ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
                {loading ? 'Création...' : 'Créer le client'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// Domain Step Component with Double Validation
function DomainStep({ data, update, domainSearch, setDomainSearch, domainPurchase, setDomainPurchase }) {
  const [confirmModal, setConfirmModal] = useState({ show: false, cartId: null, price: null, details: [] });
  const [confirming, setConfirming] = useState(false);
  const [multiResults, setMultiResults] = useState([]);
  const [selectedTld, setSelectedTld] = useState(null);

  const searchDomainMulti = async () => {
    if (!data.domaine || data.domaine.length < 3) {
      toast.error('Entrez au moins 3 caractères');
      return;
    }
    setDomainSearch({ searching: true, result: null });
    setMultiResults([]);
    setSelectedTld(null);
    try {
      const res = await api.post('/domains/search-multi', {
        domain_name: data.domaine,
        extensions: ['store', 'com', 'net', 'shop']
      });
      setMultiResults(res.data.results || []);
      setDomainSearch({ searching: false, result: { checked: true } });
    } catch (e) {
      toast.error('Erreur lors de la recherche');
      setDomainSearch({ searching: false, result: null });
    }
  };

  const preparePurchase = async (tld) => {
    setSelectedTld(tld);
    setDomainPurchase({ purchasing: true, purchased: false, orderId: null });
    try {
      const res = await api.post(`/domains/prepare-order?domain_name=${encodeURIComponent(data.domaine)}&tld=${encodeURIComponent(tld)}`);
      setConfirmModal({
        show: true,
        cartId: res.data.cart_id,
        price: res.data.total_price,
        details: res.data.details || [],
        domainName: res.data.domain_name,
        zimbraFree: res.data.zimbra_free
      });
      setDomainPurchase({ purchasing: false, purchased: false, orderId: null });
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Erreur lors de la préparation';
      toast.error(errorMsg);
      setDomainPurchase({ purchasing: false, purchased: false, orderId: null });
      setSelectedTld(null);
    }
  };

  const confirmPurchase = async () => {
    if (!confirmModal.cartId) return;
    setConfirming(true);
    try {
      const res = await api.post('/domains/confirm-order', {
        cart_id: confirmModal.cartId,
        client_id: 'temp-wizard'
      });
      toast.success(`Domaine ${res.data.domain_name} acheté avec succès !`);
      setDomainPurchase({ purchasing: false, purchased: true, orderId: res.data.order_id });
      update('domaine_order_id', res.data.order_id);
      update('email_domaine', res.data.email_to_create);
      update('zimbra_slot_included', true);
      update('email_domaine_status', 'pending_configuration');
      // Store the full domain (with tld) for later use
      update('domaine_full', res.data.domain_name);
      setConfirmModal({ show: false, cartId: null, price: null, details: [] });

      try {
        await api.post('/tasks', {
          client_id: 'temp-wizard',
          client_name: data.societe || 'Nouveau client',
          type: 'email_setup',
          domain: res.data.domain_name,
          delay_minutes: 15
        });
        toast.info('Tâche de configuration email créée - Visible sur le Dashboard dans 15 min', { duration: 6000 });
      } catch (taskErr) {
        console.error('Failed to create task:', taskErr);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Erreur lors de l'achat";
      if (e.response?.status === 403) {
        toast.error(`🛡️ ${errorMsg}`, { duration: 6000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setConfirming(false);
    }
  };

  const cancelPurchase = () => {
    setConfirmModal({ show: false, cartId: null, price: null, details: [] });
    setSelectedTld(null);
    toast.info('Achat annulé');
  };

  const formatPrice = (p) => p != null ? `${p.toFixed(2)}€` : '—';

  return (
    <div className="space-y-4">
      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="confirm-modal-overlay">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" data-testid="confirm-modal">
            <div className="bg-amber-500 text-white px-6 py-4 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <ShoppingCart size={20} />
                </div>
                <div>
                  <p className="font-bold text-lg">Confirmer l'achat</p>
                  <p className="text-amber-100 text-sm">Vérifiez les détails avant validation</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">Domaine</p>
                <p className="font-bold text-xl text-gray-900">{confirmModal.domainName}</p>
              </div>
              <div className="border border-gray-200 rounded-xl divide-y divide-gray-100">
                {confirmModal.details.length > 0 ? (
                  confirmModal.details.map((detail, idx) => (
                    <div key={`detail-${detail.description}-${idx}`} className="flex justify-between items-center px-4 py-3">
                      <span className="text-sm text-gray-700">{detail.description}</span>
                      <span className={`text-sm font-medium ${detail.price_value === 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                        {detail.price_value === 0 ? 'OFFERT' : detail.price}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-sm text-gray-700">Domaine + Zimbra Starter</span>
                    <span className="text-sm font-medium text-gray-900">{confirmModal.price}€</span>
                  </div>
                )}
                <div className="flex justify-between items-center px-4 py-3 bg-emerald-50">
                  <span className="font-bold text-gray-900">Total TTC</span>
                  <span className="font-bold text-xl text-emerald-700">{confirmModal.price}€</span>
                </div>
              </div>
              {confirmModal.zimbraFree ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-4 py-2 rounded-lg">
                  <CheckCircle size={16} />
                  <span>Zimbra Starter <strong>1 AN OFFERT</strong></span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-4 py-2 rounded-lg">
                  <AlertCircle size={16} />
                  <span>Zimbra Starter inclus — vérifiez le prix ci-dessus</span>
                </div>
              )}
              <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 px-4 py-3 rounded-lg">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>Cette action est <strong>irréversible</strong>. Le montant sera débité immédiatement.</span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={cancelPurchase}
                disabled={confirming}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
                data-testid="cancel-purchase-btn"
              >
                Annuler
              </button>
              <button
                onClick={confirmPurchase}
                disabled={confirming}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                data-testid="confirm-purchase-btn"
              >
                {confirming ? (
                  <><Loader size={16} className="animate-spin" /> Achat en cours...</>
                ) : (
                  <><Check size={16} /> Confirmer ({confirmModal.price}€)</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Globe size={20} className="text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Domaine + Email Zimbra</p>
            <p className="text-sm text-gray-600 mt-0.5">
              Choisissez l'extension la plus avantageuse — <strong>prix comparés sur 3 ans</strong>
            </p>
          </div>
        </div>
      </div>

      {!domainPurchase.purchased ? (
        <>
          <Field label="Nom de domaine">
            <div className="flex gap-2">
              <input
                className={inputCls + " flex-1"}
                value={data.domaine}
                onChange={e => {
                  update('domaine', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                  setDomainSearch({ searching: false, result: null });
                  setMultiResults([]);
                }}
                placeholder="nom-du-salon"
                data-testid="domain-input"
              />
              <button
                onClick={searchDomainMulti}
                disabled={domainSearch.searching || !data.domaine || data.domaine.length < 3}
                className="flex items-center gap-2 px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] disabled:opacity-50 transition-colors"
                data-testid="domain-search-btn"
              >
                {domainSearch.searching ? <Loader size={14} className="animate-spin" /> : <Search size={14} />}
                Comparer les prix
              </button>
            </div>
          </Field>

          {/* Multi-TLD results */}
          {multiResults.length > 0 && (
            <div className="space-y-2" data-testid="domain-results-table">
              {multiResults
                .sort((a, b) => (a.price_3y || 999) - (b.price_3y || 999))
                .map((r, idx) => {
                  const isCheapest = idx === 0 && r.available && r.price_3y != null;
                  return (
                    <div
                      key={r.tld}
                      className={`border rounded-xl p-4 transition-colors ${
                        !r.available ? 'opacity-40 bg-gray-50 border-gray-200' :
                        isCheapest ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`domain-row-${r.tld}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-base font-bold text-gray-900">{data.domaine}.<strong>{r.tld}</strong></span>
                            {isCheapest && (
                              <span className="px-2 py-0.5 bg-emerald-200 text-emerald-800 text-[10px] font-bold rounded-full">MEILLEUR PRIX</span>
                            )}
                            {!r.available && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded-full">INDISPONIBLE</span>
                            )}
                          </div>
                          {r.available && (
                            <div className="flex items-center gap-4 mt-1.5 text-sm text-gray-500">
                              <span>1ère année : <strong className="text-gray-700">{formatPrice(r.price_year)}</strong></span>
                              <span>Renouvellement : {formatPrice(r.renewal_price)}/an</span>
                            </div>
                          )}
                        </div>
                        {r.available && (
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Coût 3 ans</p>
                              <p className={`text-lg font-bold ${isCheapest ? 'text-emerald-700' : 'text-gray-900'}`}>
                                {formatPrice(r.price_3y)}
                              </p>
                            </div>
                            <button
                              onClick={() => preparePurchase(r.tld)}
                              disabled={domainPurchase.purchasing}
                              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                                isCheapest
                                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                  : 'bg-[hsl(222,47%,11%)] text-white hover:bg-[hsl(222,47%,18%)]'
                              }`}
                              data-testid={`buy-domain-${r.tld}`}
                            >
                              {domainPurchase.purchasing && selectedTld === r.tld ? (
                                <Loader size={14} className="animate-spin" />
                              ) : (
                                <ShoppingCart size={14} />
                              )}
                              Acheter
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </>
      ) : (
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-200 rounded-full flex items-center justify-center">
              <CheckCircle size={24} className="text-emerald-700" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-emerald-800 text-lg">Domaine acheté avec succès !</p>
              <div className="mt-2 space-y-1 text-sm text-emerald-700">
                <p><strong>Domaine:</strong> {data.domaine_full || `${data.domaine}.store`}</p>
                <p><strong>Commande:</strong> #{domainPurchase.orderId}</p>
                <p><strong>Email à créer:</strong> contact@{data.domaine_full || `${data.domaine}.store`}</p>
              </div>
              <p className="mt-3 text-xs text-emerald-600 bg-emerald-100 px-3 py-2 rounded-lg">
                Passez à l'étape suivante pour configurer l'email dans OVH
              </p>
            </div>
          </div>
        </div>
      )}

      {!domainPurchase.purchased && (
        <p className="text-xs text-gray-500 text-center">
          Vous pouvez passer cette étape et configurer le domaine plus tard
        </p>
      )}
    </div>
  );
}

// Email Configuration Step Component - 3 OPTIONS
function EmailConfigStep({ data, update, setData, emailConfigRef, emailCheck, setEmailCheck }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [emailForm, setEmailForm] = useState({
    email: data.email_domaine || (data.domaine_full ? `contact@${data.domaine_full}` : (data.domaine ? `contact@${data.domaine}.store` : '')),
    password: data.email_domaine_password || process.env.REACT_APP_OVH_DEFAULT_EMAIL_PASSWORD || '',
    server: data.email_domaine_server || 'ssl0.ovh.net'
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Sync emailForm changes to parent data (for when user skips test)
  const updateEmailForm = (field, value) => {
    const newForm = { ...emailForm, [field]: value };
    setEmailForm(newForm);
    setTestResult(null);
    
    // Also update parent data so it's saved even without test
    if (field === 'email') {
      update('email_domaine', value);
      const detectedServer = detectServer(value);
      newForm.server = detectedServer;
      setEmailForm(newForm);
      update('email_domaine_server', detectedServer);
    } else if (field === 'password') {
      update('email_domaine_password', value);
    } else if (field === 'server') {
      update('email_domaine_server', value);
    }
  };

  // Auto-detect server based on email domain
  const detectServer = (email) => {
    if (!email || !email.includes('@')) return 'ssl0.ovh.net';
    const domain = email.split('@')[1].toLowerCase();
    
    if (domain.includes('gmail')) return 'imap.gmail.com';
    if (domain.includes('outlook') || domain.includes('hotmail')) return 'outlook.office365.com';
    if (domain.includes('yahoo')) return 'imap.mail.yahoo.com';
    return 'ssl0.ovh.net'; // Default OVH
  };

  // Test email connection
  const testConnection = async () => {
    if (!emailForm.email || !emailForm.password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const res = await api.post('/emails/test-connection', {
        email: emailForm.email,
        password: emailForm.password,
        imap_server: emailForm.server
      });
      
      if (res.data.success) {
        setTestResult({ success: true, message: 'Connexion réussie !' });
        setEmailCheck({ checking: false, ready: true });
        
        // Update ref (synchronous - will be available in handleSubmit)
        emailConfigRef.current = {
          email: emailForm.email,
          password: emailForm.password,
          server: emailForm.server,
          status: 'active'
        };
        
        // Also update state for UI
        setData(prev => ({
          ...prev,
          email_domaine: emailForm.email,
          email_domaine_password: emailForm.password,
          email_domaine_server: emailForm.server,
          email_domaine_status: 'active'
        }));
        
        toast.success('Email configuré avec succès !');
      } else {
        setTestResult({ success: false, message: res.data.error || 'Échec de connexion' });
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Erreur de connexion';
      setTestResult({ success: false, message: errorMsg });
    } finally {
      setTesting(false);
    }
  };

  // Skip and create task for later
  const skipForLater = async () => {
    if (data.domaine && data.zimbra_slot_included) {
      // Create a pending task
      try {
        await api.post('/tasks', {
          client_id: 'temp-wizard',
          client_name: data.societe || 'Nouveau client',
          type: 'email_setup',
          domain: data.domaine_full || (data.domaine.includes('.') ? data.domaine : `${data.domaine}.store`),
          delay_minutes: 15
        });
        toast.success('Tâche créée ! Vous serez notifié sur le Dashboard.', { duration: 5000 });
      } catch (e) {
        console.error('Failed to create task:', e);
      }
    }
    update('email_domaine_status', 'pending_later');
  };

  // If already configured
  if (emailCheck.ready || data.email_domaine_status === 'active') {
    return (
      <div className="space-y-4">
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-emerald-200 rounded-full flex items-center justify-center">
              <CheckCircle size={28} className="text-emerald-700" />
            </div>
            <div>
              <p className="font-bold text-emerald-800 text-lg">Email configuré !</p>
              <p className="text-emerald-700 mt-1"><strong>{data.email_domaine}</strong></p>
              <p className="text-sm text-emerald-600 mt-2">La boîte mail est prête à utiliser dans le CRM</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Option selection screen
  if (!selectedOption) {
    return (
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Configuration Email</p>
              <p className="text-sm text-gray-600 mt-0.5">Choisissez comment configurer l'email du client</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* Option A: Email existant */}
          <button
            onClick={() => setSelectedOption('existing')}
            className="w-full text-left p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all group"
            data-testid="option-existing-email"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Mail size={20} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">J'ai un email existant</p>
                <p className="text-sm text-gray-500 mt-0.5">Raccorder un email déjà créé (OVH, Gmail, Outlook...)</p>
              </div>
              <ChevronRight size={20} className="text-gray-400 group-hover:text-blue-500 mt-2" />
            </div>
          </button>

          {/* Option B: Configurer plus tard */}
          <button
            onClick={() => {
              skipForLater();
              setSelectedOption('later');
            }}
            className="w-full text-left p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-amber-400 hover:bg-amber-50/50 transition-all group"
            data-testid="option-later"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Configurer plus tard</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {data.domaine ? 
                    "Le domaine vient d'être acheté - Vous serez notifié quand ce sera prêt (~15 min)" :
                    "Passer cette étape et configurer depuis la fiche client"
                  }
                </p>
              </div>
              <ChevronRight size={20} className="text-gray-400 group-hover:text-amber-500 mt-2" />
            </div>
          </button>

          {/* Option C: Domaine prêt */}
          {data.domaine && (
            <button
              onClick={() => setSelectedOption('ready')}
              className="w-full text-left p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-400 hover:bg-emerald-50/50 transition-all group"
              data-testid="option-domain-ready"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <CheckCircle size={20} className="text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Mon domaine est prêt</p>
                  <p className="text-sm text-gray-500 mt-0.5">J'ai déjà créé l'email dans OVH Zimbra</p>
                </div>
                <ChevronRight size={20} className="text-gray-400 group-hover:text-emerald-500 mt-2" />
              </div>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Option selected: "later" - show confirmation
  if (selectedOption === 'later') {
    return (
      <div className="space-y-4">
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center">
              <Clock size={28} className="text-amber-700" />
            </div>
            <div>
              <p className="font-bold text-amber-800 text-lg">Configuration différée</p>
              <p className="text-amber-700 mt-1">
                {data.domaine ? 
                  `Une tâche a été créée pour configurer contact@${data.domaine_full || data.domaine + '.store'}` :
                  "Vous pourrez configurer l'email depuis la fiche client"
                }
              </p>
              <p className="text-sm text-amber-600 mt-2 bg-amber-100 px-3 py-2 rounded-lg">
                Vous recevrez une notification sur le Dashboard quand ce sera prêt
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => setSelectedOption(null)}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <ChevronRight size={14} className="rotate-180" /> Retour aux options
        </button>
      </div>
    );
  }

  // Options "existing" or "ready" - show email form
  return (
    <div className="space-y-4">
      <div className={`border rounded-xl p-4 ${selectedOption === 'existing' ? 'bg-blue-50 border-blue-200' : 'bg-emerald-50 border-emerald-200'}`}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedOption === 'existing' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
            <Mail size={20} className={selectedOption === 'existing' ? 'text-blue-600' : 'text-emerald-600'} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">
              {selectedOption === 'existing' ? 'Raccorder un email existant' : 'Configurer l\'email du domaine'}
            </p>
            <p className="text-sm text-gray-600 mt-0.5">
              {selectedOption === 'existing' ? 
                'Entrez les identifiants de connexion IMAP' : 
                `Entrez les identifiants créés dans OVH pour ${data.domaine_full || data.domaine + '.store'}`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Mini guide for "ready" option */}
      {selectedOption === 'ready' && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Si vous n'avez pas encore créé l'email :</p>
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Ouvrir <a href="https://www.ovh.com/manager/#/web/zimbra" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">OVH Zimbra</a></li>
            <li>Ajouter le domaine <strong>{data.domaine_full || `${data.domaine}.store`}</strong></li>
            <li>Créer le compte <strong>contact@{data.domaine_full || `${data.domaine}.store`}</strong></li>
          </ol>
        </div>
      )}

      {/* Email form */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse email</label>
          <input
            type="email"
            value={emailForm.email}
            onChange={(e) => updateEmailForm('email', e.target.value)}
            placeholder="contact@exemple.com"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="email-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
          <input
            type="text"
            value={emailForm.password}
            onChange={(e) => updateEmailForm('password', e.target.value)}
            placeholder="Mot de passe"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="password-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Serveur IMAP</label>
          <input
            type="text"
            value={emailForm.server}
            onChange={(e) => updateEmailForm('server', e.target.value)}
            placeholder="ssl0.ovh.net"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
            data-testid="server-input"
          />
          <p className="text-xs text-gray-500 mt-1">Auto-détecté selon le domaine de l'email</p>
        </div>
      </div>

      {/* Test result */}
      {testResult && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {testResult.success ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span className="text-sm font-medium">{testResult.message}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => {
            setSelectedOption(null);
            setTestResult(null);
          }}
          className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Retour
        </button>
        <button
          onClick={testConnection}
          disabled={testing || !emailForm.email || !emailForm.password}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          data-testid="test-connection-btn"
        >
          {testing ? (
            <>
              <Loader size={16} className="animate-spin" />
              Test en cours...
            </>
          ) : (
            <>
              <RefreshCw size={16} />
              Tester la connexion
            </>
          )}
        </button>
      </div>
    </div>
  );
}


// Google Drive Step Component
function GoogleDriveStep({ data, update }) {
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(!!data.google_drive_folder_id);

  const createFolder = async () => {
    if (!data.societe) {
      toast.error('Nom de société requis');
      return;
    }
    
    setCreating(true);
    try {
      const res = await api.post('/drive/folder', {
        client_id: 'temp-wizard',
        client_name: data.societe
      });
      
      if (res.data.folder_id) {
        update('google_drive_folder_id', res.data.folder_id);
        update('google_drive_folder_url', res.data.folder_url);
        setCreated(true);
        toast.success('Dossier Google Drive créé !');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création du dossier');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <FolderOpen size={20} className="text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Dossier Google Drive</p>
            <p className="text-sm text-gray-600 mt-0.5">
              Créez un dossier dédié pour stocker les documents du client
            </p>
          </div>
        </div>
      </div>

      {!created ? (
        <>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Nom du dossier :</strong> {data.societe || '(Nom de la société)'}
            </p>
            <p className="text-xs text-gray-500">
              Le dossier sera créé dans votre Google Drive partagé
            </p>
          </div>
          
          <button
            onClick={createFolder}
            disabled={creating || !data.societe}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            data-testid="create-gdrive-btn"
          >
            {creating ? (
              <Loader size={16} className="animate-spin" />
            ) : (
              <FolderOpen size={16} />
            )}
            {creating ? 'Création...' : 'Créer le dossier'}
          </button>
        </>
      ) : (
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-200 rounded-full flex items-center justify-center">
              <CheckCircle size={24} className="text-emerald-700" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-emerald-800">Dossier créé avec succès !</p>
              <p className="text-sm text-emerald-700 mt-1">
                <strong>{data.societe}</strong>
              </p>
              {data.google_drive_folder_url && (
                <a 
                  href={data.google_drive_folder_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  <ExternalLink size={14} />
                  Ouvrir le dossier
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center">
        Vous pouvez passer cette étape - le dossier sera créé automatiquement à la fin
      </p>
    </div>
  );
}
